import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { useAppDispatch, useAppSelector } from '../../../commons/store/hooks';
import { selectSecurityContext } from '../../../commons/selectors/selectors';
import React, { useEffect, useState } from 'react';
import { invalidateAteAgreement, signAteAgreement } from './TraceabilityActions';
import { isOfflineSignature, SignatureOrOffline } from '../../../commons/model/common';
import { AgreementSignature } from '../../../commons/model/Traceability';
import List from '../../../commons/components/list/List';
import CheckboxFormComponent from '../../../commons/components/checkbox/CheckboxFormComponent';
import { withDerivedProps } from '../../../commons/components/hoc';
import SignatureUtils from '../../../commons/model/SignatureUtils';
import { validateAteAgreementForm } from '../../../commons/validation/validationUtils';

export type AteAgreementFormData = {
  received: boolean;
  exempt: boolean;
};

export type AteAgreementSubsectionProps = {
  signatures: SignatureOrOffline<AgreementSignature>[];
  editable: boolean;
};

const AteAgreementSubsection = ({
  signatures,
  editable,
  handleSubmit,
  change,
  error,
  submitFailed,
}: AteAgreementSubsectionProps & InjectedFormProps<AteAgreementFormData>) => {
  const { username, email } = useAppSelector(selectSecurityContext);
  const dispatch = useAppDispatch();
  const [formOpen, setFormOpen] = useState(editable && signatures.length === 0);

  useEffect(() => {
    setFormOpen(editable && signatures.length === 0);
  }, [editable, signatures]);

  const doHandleSubmit = (formValues: AteAgreementFormData) => {
    dispatch(signAteAgreement(formValues.exempt));
  };

  const handleInvalidate = (signature: SignatureOrOffline<AgreementSignature>) => () => {
    dispatch(invalidateAteAgreement(isOfflineSignature(signature) ? null : signature.userId));
  };

  return (
    <>
      {signatures.map((signature, index) => (
        <div className="subsection-content" key={index}>
          <List
            items={[
              {
                key: 'Observations',
                value: <div>{signature.exempt ? "Dispense d'incorporation" : 'Néant'}</div>,
              },
              ...SignatureUtils.basicSignatureFields(signature, username),
            ]}
          />
          {editable && (
            <button className="btn btn-round small reset" onClick={handleInvalidate(signature)} type="button" />
          )}
        </div>
      ))}
      {editable &&
        (formOpen ? (
          <form className="agreement-form" autoComplete="off" noValidate onSubmit={handleSubmit(doHandleSubmit)}>
            <Field
              component={CheckboxFormComponent}
              name="received"
              label="Accord reçu"
              onCheck={(checked: boolean) => {
                if (checked) {
                  change('exempt', false);
                }
              }}
            />
            <Field
              component={CheckboxFormComponent}
              name="exempt"
              label="Dispense d'incorporation"
              onCheck={(checked: boolean) => {
                if (checked) {
                  change('received', false);
                }
              }}
            />
            {submitFailed && error && <div className="error-message">{error}</div>}
            <div className="buttons">
              <button className="btn btn-accent" type="submit">
                Valider et signer
              </button>
            </div>
          </form>
        ) : (
          signatures.every((signature) => !isOfflineSignature(signature) && signature.userEmail !== email) && (
            <button className="btn btn-round small plus" onClick={() => setFormOpen(true)} type="button" />
          )
        ))}
      {!editable && signatures.length === 0 && <div className="empty">Non signé</div>}
    </>
  );
};

const transformProps = ({ signatures }: AteAgreementSubsectionProps) => {
  const lastSignature = signatures[signatures.length - 1];
  return {
    initialValues: {
      received: lastSignature && !lastSignature.exempt,
      exempt: lastSignature?.exempt ?? false,
    },
  };
};

export default withDerivedProps(transformProps)(
  reduxForm<AteAgreementFormData, AteAgreementSubsectionProps>({
    form: 'traceabilityAgreementForm',
    initialValues: {
      received: false,
      exempt: false,
    },
    validate: validateAteAgreementForm,
  })(AteAgreementSubsection as any) as any,
);
